import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { REGISTERS } from "../lib/registers";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { RegisterTable } from "./RegisterTable";
import { InspectionHistory } from "./InspectionHistory";

export const PageHeader = ({ eyebrow, title, description, children }) => (
  <div className="fade-up mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
    <div>
      <p className="eyebrow text-rose-400">{eyebrow}</p>
      <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl" data-testid="page-title">{title}</h1>
      {description && <p className="mt-1 max-w-2xl text-sm text-slate-400">{description}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const StartInspections = ({ templates }) => {
  const [tpls, setTpls] = useState({});
  const nav = useNavigate();
  const { canWrite } = useAuth();
  useEffect(() => {
    api.get("/checklists").then((r) => { setTpls(r.data); localStorage.setItem("eps_checklists", JSON.stringify(r.data)); }).catch(() => setTpls(JSON.parse(localStorage.getItem("eps_checklists") || "{}")));
  }, []);
  if (!canWrite || !templates?.length) return null;
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {templates.map((t) => (
        <button key={t} onClick={() => nav(`/inspect/${t}`)} className="panel group flex items-center gap-3 p-4 text-left transition-colors hover:border-rose-500/60" data-testid={`start-inspection-${t}`}>
          <ClipboardCheck className="h-6 w-6 text-rose-400 transition-transform group-hover:scale-110" />
          <span>
            <span className="block text-sm font-semibold">{tpls[t]?.name || t}</span>
            <span className="eyebrow text-[10px]">Every {tpls[t]?.frequency_days || "-"} day(s)</span>
          </span>
        </button>
      ))}
    </div>
  );
};

export default function ModulePage({ eyebrow, title, description, module, templates, tabs, summary }) {
  const [params] = useSearchParams();
  const tabParam = params.get("tab") || (params.get("wo") ? tabs[0].key : null);
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      {summary}
      <StartInspections templates={templates} />
      <Tabs key={tabParam || "default"} defaultValue={tabs.some((t) => t.key === tabParam) ? tabParam : tabs[0].key}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start bg-slate-900">
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="h-10 data-[state=active]:bg-slate-700" data-testid={`tab-${t.key}`}>{t.label || REGISTERS[t.key]?.title}</TabsTrigger>
          ))}
          <TabsTrigger value="inspections" className="h-10 data-[state=active]:bg-slate-700" data-testid="tab-inspections">Inspection history</TabsTrigger>
        </TabsList>
        {tabs.map((t) => (
          <TabsContent key={t.key} value={t.key}>{t.element || <RegisterTable kind={t.key} />}</TabsContent>
        ))}
        <TabsContent value="inspections"><InspectionHistory module={module} /></TabsContent>
      </Tabs>
    </div>
  );
}
